import * as React from "react";
import {inject, observer} from "mobx-react";
import {action} from "mobx";
import styled from "styled-components";
import type {IFieldAdapter, IFieldPointer} from "../../../../data/models/field/Field";
import {FieldDataTypes} from "../../../../data/models/field/FieldDataTypes";
import type {IFilterRow, FilterType, IFilterRowFilter} from "../../../../data/models/filter/Filter";
import {LogicalSeparator} from "../../../../data/models/filter/LogicalSeparator";
import {LogicalSeparators} from "../../../../data/models/filter/LogicalSeparators";
import type {FilterOperator} from "../../../../data/models/filter/operator/FilterOperator";
import type {AppState} from "../../../../data/state/AppState";
import type {XyiconFeature} from "../../../../generated/api/base";
import {ObjectUtils} from "../../../../utils/data/ObjectUtils";
import {ArrayUtils} from "../../../../utils/data/array/ArrayUtils";
import {StringUtils} from "../../../../utils/data/string/StringUtils";
import {ButtonV5} from "../../button/ButtonV5";
import CirclePlusIcon from "../../icons/circle-plus.svg?react";
import {ToggleOptionV5} from "../../details/ToggleOptionV5";
import {baseDistance, FlexCenter, radius, VerticalFlexStyle} from "../../styles/styles";
import {colorPalette} from "../../styles/colorPalette";
import {FilterOperatorsV5} from "../operator/FilterOperatorsV5";
import {AdvancedFilterFieldV5} from "./AdvancedFilterFieldV5";

interface IAdvancedFilterEditorProps {
	readonly autoUpdate?: boolean;
	readonly features: XyiconFeature[];
	readonly filters: IFilterRow[];
	readonly localFilters?: IFilterRow[];
	readonly operatorFilter?: (operator: FilterOperator) => boolean;
	readonly fieldFilter?: (field: IFieldAdapter) => boolean;
	readonly syncFilters?: (isLoaderNeeded?: boolean) => void;
	readonly syncLocalFilters?: (filters: IFilterRow[]) => void;
	readonly multiplePortfolios?: boolean;
	readonly appState?: AppState;
	readonly linkedFields?: boolean;
}

const advancedFilterPlaceholderKey = "__advancedFilterPlaceholder" as const;
type AdvancedFilterPlaceholderRow = IFilterRowFilter & {[advancedFilterPlaceholderKey]: true};
type AdvancedFilterPlaceholderCandidate = IFilterRowFilter & {[advancedFilterPlaceholderKey]?: true};

const markPlaceholderRow = (row: IFilterRowFilter) => {
	Object.defineProperty(row, advancedFilterPlaceholderKey, {
		value: true,
		enumerable: false,
		configurable: true,
	});
};

const clearPlaceholderRow = (row: IFilterRowFilter) => {
	delete (row as AdvancedFilterPlaceholderCandidate)[advancedFilterPlaceholderKey];
};

const isPlaceholderRow = (row: IFilterRowFilter): row is AdvancedFilterPlaceholderRow => {
	return !!(row as AdvancedFilterPlaceholderRow)[advancedFilterPlaceholderKey];
};

const isValidAdvancedFilter = (row: IFilterRowFilter) => {
	if (isPlaceholderRow(row)) {
		return false;
	}

	return !!FilterOperatorsV5.map[row.value.operator]?.validator(row.value.param);
};

export const getApplicableAdvancedFilterRows = (filters: IFilterRow[]) => {
	const rows: IFilterRow[] = [];

	filters.forEach((row, index) => {
		if (row.type !== "filter" || !isValidAdvancedFilter(row)) {
			return;
		}

		if (rows.length > 0) {
			const separator = filters[index - 1];
			rows.push({
				type: "separator",
				value: separator?.type === "separator" ? separator.value : LogicalSeparator.AND,
			});
		}

		rows.push(ObjectUtils.deepClone(row));
	});

	return rows;
};

export const hasAdvancedFilterDraftRows = (filters: IFilterRow[]) => {
	return filters.some((row) => row.type === "filter" && !isPlaceholderRow(row) && !isValidAdvancedFilter(row));
};

@inject("appState")
@observer
export class AdvancedFilterEditorV5 extends React.Component<IAdvancedFilterEditorProps> {
	public readonly type: FilterType = "advanced";

	@action
	public onAddRow = () => {
		this.addDefaultRow(this.filters);
	};

	private addDefaultRow(rows: IFilterRow[], isPlaceholder = false) {
		const field = this.getFields()[0];

		if (!field) {
			return undefined;
		}

		const operatorFilter = this.props.operatorFilter;
		const operator = FieldDataTypes.map[field.dataType]
			.operators(field.dataTypeSettings)
			.sort(StringUtils.sortIgnoreCase) // SelectInput orders by sort
			.find((op) => (operatorFilter ? operatorFilter(op) : true));

		if (!operator) {
			return undefined;
		}

		if (rows.length > 0) {
			rows.push({
				type: "separator",
				value: LogicalSeparator.AND,
			});
		}

		const row: IFilterRowFilter = {
			type: "filter",
			value: {
				field: field.refId,
				operator,
				param: undefined,
			},
		};

		rows.push(row);

		if (isPlaceholder) {
			markPlaceholderRow(row);
		}

		return row;
	}

	private getFields() {
		const {fieldFilter, features, multiplePortfolios, appState} = this.props;
		let fields = appState.actions.filterActions.getFieldsForFilters(features, undefined, multiplePortfolios) || [];

		if (fieldFilter) {
			fields = fields.filter(fieldFilter);
		}
		return fields;
	}

	private getFieldRefIds(): IFieldPointer[] {
		return this.getFields().map((f) => f.refId);
	}

	private onChangeField = (row: IFilterRowFilter, fieldPointer: IFieldPointer) => {
		clearPlaceholderRow(row);
		const field = this.props.appState.actions.getFieldByRefId(fieldPointer);

		if (field) {
			row.value.field = fieldPointer;
			// Reset operator to the first available value for this dataType
			let operators = FieldDataTypes.map[field.dataType].operators(field.dataTypeSettings);

			if (this.props.operatorFilter) {
				operators = operators.filter(this.props.operatorFilter);
			}
			this.onChangeOperator(row, operators[0]);
		}
	};

	private onChangeOperator = (row: IFilterRowFilter, operator: FilterOperator) => {
		clearPlaceholderRow(row);
		row.value.operator = operator;
		row.value.param = undefined;
	};

	private onChangeParams = (row: IFilterRowFilter, param: any) => {
		clearPlaceholderRow(row);
		row.value.param = param;
	};

	private onRemoveField = (index: number) => {
		if (this.getFilterRowCount() <= 1) {
			this.filters.length = 0;
			this.addDefaultRow(this.filters, true);
			return;
		}

		const row = this.filters[index];
		if (row?.type === "filter") {
			clearPlaceholderRow(row);
		}

		if (index >= 0) {
			this.filters.splice(index, 1);
		}

		// remove next separator if any
		const nextSeparator = this.filters[index];

		if (nextSeparator?.type === "separator") {
			this.filters.splice(index, 1);
		}

		// remove any last separators (normally this shouldn't run)
		while (this.filters[this.filters.length - 1]?.type === "separator") {
			this.filters.pop();
		}
	};

	public clearAll = () => {
		this.filters.length = 0;
		this.addDefaultRow(this.filters, true);
		this.props.syncFilters?.();
	};

	public resetTo(filters: IFilterRow[]) {
		const localFilters = ObjectUtils.deepClone(filters);
		const shouldAddDefaultRow = localFilters.length === 0;

		if (shouldAddDefaultRow) {
			this.addDefaultRow(localFilters, true);
		}

		if (shouldAddDefaultRow || !ObjectUtils.compare(this.filters, localFilters)) {
			this.props.syncLocalFilters?.(localFilters);
		}

		if (!ObjectUtils.compare(this.props.filters, filters)) {
			ArrayUtils.copy(this.props.filters, filters);
		}
	}

	public applyAll() {
		ArrayUtils.replaceObservable(this.props.filters, this.getFiltersToApply());
	}

	public getFiltersToApply() {
		return getApplicableAdvancedFilterRows(this.filters);
	}

	private getFilterRowCount() {
		return this.filters.filter((row) => row.type === "filter").length;
	}

	private get filters() {
		const {autoUpdate, localFilters, filters} = this.props;

		return autoUpdate ? filters : localFilters;
	}

	override componentDidMount(): void {
		if (this.filters.length === 0) {
			this.addDefaultRow(this.filters, true);
		}
	}

	public override render() {
		const {operatorFilter, linkedFields} = this.props;
		const fieldRefIds = this.getFieldRefIds();

		return (
			<AdvancedFilterEditorStyled className="AdvancedFilterEditor">
				<FilterFieldsContainer>
					{this.filters.map((row, index) => {
						if (row.type === "filter") {
							return (
								<AdvancedFilterFieldV5
									key={index}
									fieldRefIds={fieldRefIds}
									filter={row.value}
									onChangeField={(field) => this.onChangeField(row, field)}
									onChangeOperator={(operator) => this.onChangeOperator(row, operator)}
									onChangeParams={(params) => this.onChangeParams(row, params)}
									onRemove={() => this.onRemoveField(index)}
									operatorFilter={operatorFilter}
									linkedFields={linkedFields}
								>
									<div>
										{this.filters[index - 1]?.type === "separator" ? (
											<ToggleOptionV5
												key={index - 1}
												options={LogicalSeparators.array}
												render={(separator) => separator.label}
												onChange={(value) => {
													this.filters[index - 1].value = value.id;
													this.forceUpdate();
												}}
												selectedOption={LogicalSeparators.array.find((separator) => separator.id === this.filters[index - 1].value)?.label}
											/>
										) : (
											<span>Where</span>
										)}
									</div>
								</AdvancedFilterFieldV5>
							);
						}
					})}
				</FilterFieldsContainer>
				<FlexCenter style={{justifyContent: "space-between"}}>
					{this.filters.length > 0 && (
						<ButtonV5
							label="Clear All"
							className="naked"
							onClick={this.clearAll}
							type="tertiary"
							style={{color: colorPalette.gray.c950, padding: `0 ${baseDistance.sm}`}}
						/>
					)}
					<ButtonV5
						label="ADD"
						type="secondary"
						onClick={this.onAddRow}
					>
						<CirclePlusIcon />{" "}
					</ButtonV5>
				</FlexCenter>
			</AdvancedFilterEditorStyled>
		);
	}
}

const AdvancedFilterEditorStyled = styled.div`
	${VerticalFlexStyle};
	gap: ${baseDistance.sm};
	min-width: 620px;
`;

const FilterFieldsContainer = styled.div`
	${VerticalFlexStyle};
	background-color: ${colorPalette.gray.c100Light};
	gap: ${baseDistance.sm};
	border-radius: ${radius.sm};
	padding: ${baseDistance.sm};
	overflow: auto;
`;
